import React, { useMemo, useState } from 'react';
import {
	Box,
	Paper,
	Typography,
	Grid,
	Checkbox,
	FormControlLabel,
	CircularProgress,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import { useQuery, useQueryClient } from 'react-query';
import { getDays } from 'core/API/organizationSettings';
import { apiService } from 'core/services/apiService';
import { UserAvailability } from 'core/interfaces/userInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { Button } from 'components/ui/button';
import CurrentAvailabilityList from 'module/schedule/components/CurrentAvailabilityList/CurrentAvailabilityList';
import {
	buildAllWeekDays,
	getLatestAvailabilityForDay,
	toDayjsAvailabilityTime,
} from 'module/schedule/utils/availabilityDisplay';
import {
	hasSavedAvailability,
	saveOnboardingAvailability,
} from '../utils/onboardingAvailability';
import style from './EmployeeOnboardingAvailabilityForm.module.scss';

interface EmployeeOnboardingAvailabilityFormProps {
	workplaceId: number;
	editable: boolean;
	onSubmit: () => void;
}

export function EmployeeOnboardingAvailabilityForm({
	workplaceId,
	editable,
	onSubmit,
}: EmployeeOnboardingAvailabilityFormProps): JSX.Element {
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data: daysData, isLoading: daysLoading } = useQuery(['days'], () => getDays());
	const allDays = useMemo(() => buildAllWeekDays(daysData), [daysData]);

	const {
		data: userData,
		isLoading: userLoading,
		refetch: refetchUser,
	} = useQuery(
		['user-availability', 'me', 'onboarding'],
		async () => {
			const response = await apiService.get('/users/me');
			return apiService.responseHandler(response);
		},
		{
			refetchOnMount: 'always',
			staleTime: 0,
		},
	);

	const currentAvailability: UserAvailability[] = useMemo(
		() => userData?.userAvailability || [],
		[userData?.userAvailability],
	);

	const initialValues = useMemo(
		() => ({
			availability: allDays.map((day) => {
				const latestAvail = getLatestAvailabilityForDay(currentAvailability, day);

				if (latestAvail) {
					const startTime = toDayjsAvailabilityTime(latestAvail.startAt);
					const endTime = toDayjsAvailabilityTime(latestAvail.endAt);
					const isUnavailable =
						startTime && endTime && startTime.format('HH:mm') === endTime.format('HH:mm');

					if (isUnavailable) {
						return {
							day,
							enabled: false,
							startTime: null,
							endTime: null,
						};
					}

					return {
						day,
						enabled: Boolean(startTime && endTime),
						startTime,
						endTime,
					};
				}

				return {
					day,
					enabled: false,
					startTime: null,
					endTime: null,
				};
			}),
		}),
		[allDays, currentAvailability],
	);

	const handleSubmit = async (values: typeof initialValues) => {
		if (!editable) return;

		const enabledDays = values.availability.filter((d) => d.enabled);
		if (enabledDays.length === 0) {
			showToast('error', 'Please select at least one day you are available');
			return;
		}

		const invalidDays = values.availability.filter(
			(d) => d.enabled && (!d.startTime || !d.endTime),
		);
		if (invalidDays.length > 0) {
			const dayNames = invalidDays.map((d) => d.day.label).join(', ');
			showToast('error', `Please set start and end times for: ${dayNames}`);
			return;
		}

		// Overnight ranges (e.g. 10:00 PM – 2:00 AM) are valid — matching AvailabilityForm / Scheduling.

		try {
			setIsSubmitting(true);
			await saveOnboardingAvailability({
				workplaceId,
				days: values.availability,
				currentAvailability,
			});
			await queryClient.invalidateQueries(['user-availability']);
			await refetchUser();
			showToast('success', 'Availability saved successfully!');
			onSubmit();
		} catch (error: any) {
			console.error('Error saving onboarding availability:', error);
			showToast(
				'error',
				error?.response?.data?.message ||
					error?.message ||
					'Failed to save availability. Please try again.',
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const alreadySaved = hasSavedAvailability(currentAvailability);

	return (
		<Paper sx={{ p: 3 }}>
			<Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
				Set Your Availability
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Tell us which days and times you are typically available to work. This information
				will be used by the Scheduling module and can be updated later from your profile.
			</Typography>

			{daysLoading || userLoading ? (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
					<CircularProgress size={24} />
					<Typography sx={{ ml: 2 }}>Loading availability options...</Typography>
				</Box>
			) : (
				<Formik
					initialValues={initialValues}
					onSubmit={handleSubmit}
					enableReinitialize
				>
					{({ values, setFieldValue, handleSubmit: formikSubmit }) => (
						<Form>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								{alreadySaved && (
									<CurrentAvailabilityList
										days={allDays}
										currentAvailability={currentAvailability}
										subtitle="Your saved availability (you can update it below)"
									/>
								)}

								<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
									Weekly Availability
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Select the days you are available and specify your preferred time range.
								</Typography>

								{values.availability.map((d, index) => (
									<Grid
										container
										spacing={2}
										alignItems="center"
										key={d.day['@id'] || d.day.id}
										sx={{ mb: 1, borderRadius: 1, p: 1 }}
									>
										<Grid item xs={12} sm={4}>
											<FormControlLabel
												control={
													<Checkbox
														checked={d.enabled}
														disabled={!editable || isSubmitting}
														onChange={(e) => {
															const updated = [...values.availability];
															updated[index] = {
																...updated[index],
																enabled: e.target.checked,
															};

															if (!e.target.checked) {
																updated[index].startTime = null;
																updated[index].endTime = null;
															} else {
																const lastFilledDay = [...updated]
																	.slice(0, index)
																	.reverse()
																	.find(
																		(day) =>
																			day.enabled && day.startTime && day.endTime,
																	);
																if (lastFilledDay) {
																	updated[index].startTime = dayjs(
																		lastFilledDay.startTime,
																	);
																	updated[index].endTime = dayjs(lastFilledDay.endTime);
																}
															}
															setFieldValue('availability', updated);
														}}
													/>
												}
												label={d.day.label}
											/>
										</Grid>
										<Grid item xs={6} sm={4}>
											<TimePicker
												label="Start Time"
												ampm
												format="hh:mm A"
												value={d.startTime}
												disabled={!editable || !d.enabled || isSubmitting}
												onChange={(newTime) => {
													const updated = [...values.availability];
													updated[index] = {
														...updated[index],
														startTime: newTime,
													};
													setFieldValue('availability', updated);
												}}
												slotProps={{
													textField: { fullWidth: true },
												}}
											/>
										</Grid>
										<Grid item xs={6} sm={4}>
											<TimePicker
												label="End Time"
												ampm
												format="hh:mm A"
												value={d.endTime}
												disabled={!editable || !d.enabled || isSubmitting}
												onChange={(newTime) => {
													const updated = [...values.availability];
													updated[index] = {
														...updated[index],
														endTime: newTime,
													};
													setFieldValue('availability', updated);
												}}
												slotProps={{
													textField: { fullWidth: true },
												}}
											/>
										</Grid>
									</Grid>
								))}

								{editable && (
									<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
										<Button
											type="button"
											disabled={isSubmitting}
											className={style.SubmitButton}
											style={{ padding: '5px', borderRadius: '5px' }}
											onClick={() => formikSubmit()}
										>
											{isSubmitting ? <CircularProgress size={24} /> : 'Save & Continue'}
										</Button>
									</Box>
								)}
							</LocalizationProvider>
						</Form>
					)}
				</Formik>
			)}
		</Paper>
	);
}
